import React from 'react';
import { centsToDollarsDisplay, integerDisplay, percentageDisplay } from '../displayFormatters';
import './Financials.css'
import Info from './Info'
import Summary from './Summary'
import { Row, Col, Spin, Collapse, List, Typography } from 'antd';
import { FormattedData } from './financialsModel';
import { JobFinancialsData } from '../Items/JobCosting/jobCostingModel';

export function getFormattedData(data: JobFinancialsData): FormattedData {
  function getInfoStyle(estimatedValue: number, actualValue: number) {
    return {
      up: actualValue > estimatedValue,
      down: actualValue < estimatedValue,
      positive: actualValue > estimatedValue,
      negative: actualValue < estimatedValue
    }
  }
  function getSumaryStyle(estimatedValue: number, actualValue: number) {
    return {
      up: actualValue > estimatedValue,
      down: actualValue < estimatedValue,
      positive: actualValue < estimatedValue,
      negative: actualValue > estimatedValue
    }
  }

  const formattedData: FormattedData = {
    info: {
      price: [
        {
          type: "estimated",
          amount: `${centsToDollarsDisplay(data.price.estimated)}`
        },
        {
          type: "actual",
          amount: `${centsToDollarsDisplay(data.price.actual)}`
        },
        {
          type: "final",
          amount: `${centsToDollarsDisplay(data.price.final)}`,
          style: getInfoStyle(data.price.estimated, data.price.final)
        }
      ],
      grossProfit: [
        {
          type: "estimated",
          amount: percentageDisplay(data.grossProfit.estimated.percentage)
        },
        {
          type: "estimated",
          amount: `${centsToDollarsDisplay(data.grossProfit.estimated.amount)}`
        },
        {
          type: "final",
          amount: percentageDisplay(data.grossProfit.final.percentage),
          style: getInfoStyle(data.grossProfit.estimated.percentage, data.grossProfit.final.percentage)
        },
        {
          type: "final",
          amount: `${centsToDollarsDisplay(data.grossProfit.final.amount)}`,
          style: getInfoStyle(data.grossProfit.estimated.amount, data.grossProfit.final.amount)
        }
      ],
      netProfit: [
        {
          type: "estimated",
          amount: percentageDisplay(data.netProfit.estimated.percentage)
        },
        {
          type: "estimated",
          amount: `${centsToDollarsDisplay(data.netProfit.estimated.amount)}`
        },
        {
          type: "final",
          amount: percentageDisplay(data.netProfit.final.percentage),
          style: getInfoStyle(data.netProfit.estimated.percentage, data.netProfit.final.percentage)
        },
        {
          type: "final",
          amount: `${centsToDollarsDisplay(data.netProfit.final.amount)}`,
          style: getInfoStyle(data.netProfit.estimated.amount, data.netProfit.final.amount)
        }
      ]
    },
    summary: {
      estimated: [
        {
          title: "Labor",
          amount: `${centsToDollarsDisplay(data.estimated.labor)}`
        },
        {
          title: "Materials",
          amount: `${centsToDollarsDisplay(data.estimated.materials)}`
        },
        {
          title: "Equipment",
          amount: `${centsToDollarsDisplay(data.estimated.equipment)}`
        },
        {
          title: "Subcontracting",
          amount: `${centsToDollarsDisplay(data.estimated.subcontracting)}`
        },
        {
          title: "Overhead",
          amount: `${centsToDollarsDisplay(data.estimated.overhead)}`
        },
        {
          title: "Breakeven",
          amount: `${centsToDollarsDisplay(data.estimated.breakeven)}`
        }
      ],
      actual: [
        {
          title: "Labor",
          amount: `${centsToDollarsDisplay(data.actual.labor)}`,
          style: getSumaryStyle(data.estimated.labor, data.actual.labor)
        },
        {
          title: "Materials",
          amount: `${centsToDollarsDisplay(data.actual.materials)}`,
          style: getSumaryStyle(data.estimated.materials, data.actual.materials)
        },
        {
          title: "Equipment",
          amount: `${centsToDollarsDisplay(data.actual.equipment)}`,
          style: getSumaryStyle(data.estimated.equipment, data.actual.equipment)
        },
        {
          title: "Subcontracting",
          amount: `${centsToDollarsDisplay(data.actual.subcontracting)}`,
          style: getSumaryStyle(data.estimated.subcontracting, data.actual.subcontracting)
        },
        {
          title: "Overhead",
          amount: `${centsToDollarsDisplay(data.actual.overhead)}`,
          style: getSumaryStyle(data.estimated.overhead, data.actual.overhead)
        },
        {
          title: "Breakeven",
          amount: `${centsToDollarsDisplay(data.actual.breakeven)}`,
          style: getSumaryStyle(data.estimated.breakeven, data.actual.breakeven)
        }
      ],
      unbillable: [
        {
          title: "Labor",
          amount: `${centsToDollarsDisplay(data.unbillable.labor)}`
        },
        {
          title: "Materials",
          amount: `${centsToDollarsDisplay(data.unbillable.materials)}`
        },
        {
          title: "Equipment",
          amount: `${centsToDollarsDisplay(data.unbillable.equipment)}`
        },
        {
          title: "Subcontracting",
          amount: `${centsToDollarsDisplay(data.unbillable.subcontracting)}`
        }
      ],
      laborHours: [
        {
          title: "Actual",
          amount: `${integerDisplay(data.actual.laborHours)}`
        },
        {
          title: "Estimated",
          amount: `${integerDisplay(data.estimated.laborHours)}`
        },
        {
          title: "Unbillable",
          amount: `${integerDisplay(data.unbillable.laborHours)}`
        },
      ]
    }
  }
  return formattedData
}

export type Props = {
  data?: JobFinancialsData
  title?: string
  renderFilters: () => React.ReactNode
  displayLaborHours?: boolean
  displayJobs?: boolean
}

export default function Financials({ data, title, renderFilters, displayLaborHours, displayJobs }: Props) {

  const [viewData, setViewData] = React.useState<FormattedData | undefined>()

  React.useEffect(() => {
    if (data)
      setViewData(getFormattedData(data))
  }, [data])

  return (
    <div className="financials">
      <div className="title">
        <h1>{title}</h1>
      </div>
      {viewData &&
        (<>
          {renderFilters?.()}
          <Row gutter={28}>
            <Col span={10}>
              <Info data={viewData.info} />
            </Col>
            <Col span={14}>
              <Summary data={viewData.summary} displayLaborHours={displayLaborHours} />
            </Col>
          </Row>
          {(displayJobs && data?.jobs) &&
            <Collapse style={{ textAlign: 'initial' }} items={[
              {
                key: 'jobs',
                label: 'Jobs included in this data:',
                children: <List dataSource={data.jobs} renderItem={item => {
                  return <List.Item>
                    <Typography.Link href={`/#!/${item.url}`}>{item.number}: {item.name}</Typography.Link>
                  </List.Item>
                }} />
              }
            ]} />
          }
        </>)
      }
      {!viewData &&
        (<Spin />)
      }
    </div>
  );
}
